import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw } from "lucide-react";

import ContractorCard from "../components/contracts/ContractorCard";
import InviteCompanyModal from "../components/contracts/InviteCompanyModal";

import { getContracts, deleteContract } from "../services/contractService";
import apiClient from "../services/apiClient";
import { useCompany } from "../context/CompanyContext";

/* ===================================================== */
/* Contractors Page (MERGED + FINAL)                     */
/* ===================================================== */

export default function ContractorsPage() {
  const { activeCompany } = useCompany();

  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [active, setActive] = useState([]);
  const [rejected, setRejected] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [refreshing, setRefreshing] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [inviteOpen, setInviteOpen] = useState(false);

  /* ===================================================== */
  /* Normalize Contracts (SAFE)                            */
  /* ===================================================== */

  const normalizeContracts = (data) => {
    // NEW API format (preferred)
    if (data?.incoming || data?.outgoing || data?.active) {
      return {
        incoming: data.incoming || [],
        outgoing: data.outgoing || [],
        active: data.active || [],
        rejected: data.rejected || [],
      };
    }

    // FALLBACK: flat list
    const list = Array.isArray(data) ? data : [];

    return {
      incoming: list.filter((c) => c.status === "invited" && c.direction === "incoming"),
      outgoing: list.filter((c) => c.status === "invited" && c.direction === "outgoing"),
      active: list.filter((c) => c.status === "active"),
      rejected: list.filter((c) => c.status === "rejected"),
    };
  };

  /* ===================================================== */
  /* Load Contracts                                        */
  /* ===================================================== */

  const loadContracts = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const data = await getContracts();
      const normalized = normalizeContracts(data);

      setIncoming(normalized.incoming);
      setOutgoing(normalized.outgoing);
      setActive(normalized.active);
      setRejected(normalized.rejected);

    } catch (err) {
      setError(err?.message || "Failed to load contractors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeCompany?._id) {
      loadContracts();
    }
  }, [activeCompany?._id, loadContracts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadContracts();
    setRefreshing(false);
  };

  /* ===================================================== */
  /* Actions                                               */
  /* ===================================================== */

  const handleAccept = async (contract) => {
    setActionLoadingId(contract._id);

    try {
      await apiClient.post(`/contracts/${contract._id}/accept`);

      setIncoming((prev) =>
        prev.filter((c) => c._id !== contract._id)
      );

      setActive((prev) => [
        { ...contract, status: "active" },
        ...prev,
      ]);

    } catch (err) {
      alert(err?.message || "Failed to accept");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (contract) => {
    setActionLoadingId(contract._id);

    try {
      await apiClient.post(`/contracts/${contract._id}/reject`);

      setIncoming((prev) =>
        prev.filter((c) => c._id !== contract._id)
      );

      setRejected((prev) => [
        { ...contract, status: "rejected" },
        ...prev,
      ]);

    } catch (err) {
      alert(err?.message || "Failed to reject");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCancel = async (contract) => {
    setActionLoadingId(contract._id);

    try {
      await apiClient.post(`/contracts/${contract._id}/cancel`);

      setOutgoing((prev) =>
        prev.filter((c) => c._id !== contract._id)
      );

    } catch (err) {
      alert(err?.message || "Failed to cancel");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (contract) => {
    if (!window.confirm("Remove this contractor?")) return;

    setActionLoadingId(contract._id);

    try {
      await deleteContract(contract._id);

      setActive((prev) =>
        prev.filter((c) => c._id !== contract._id)
      );

    } catch (err) {
      alert(err?.message || "Failed to remove contractor");
    } finally {
      setActionLoadingId(null);
    }
  };

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  if (!activeCompany) return null;

  return (
    <div className="mx-auto max-w-6xl p-6 space-y-6">

      {/* HEADER */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Contractors</h1>

        <div className="ml-auto flex items-center gap-2">

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <RefreshCw
              className={`w-4 h-4 ${
                refreshing ? "animate-spin" : ""
              }`}
            />
          </button>

          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
          >
            <Plus className="w-4 h-4" />
            Invite Contractor
          </button>

        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* LOADING */}
      {loading ? (
        <div className="rounded-xl border bg-white p-6 text-sm text-gray-500">
          Loading contractors…
        </div>
      ) : (
        <>
          {incoming.length > 0 && (
            <Section title="Incoming Requests">
              {incoming.map((c) => (
                <ContractorCard
                  key={c._id}
                  contractor={c}
                  variant="incoming"
                  onAccept={handleAccept}
                  onReject={handleReject}
                  loadingId={actionLoadingId}
                />
              ))}
            </Section>
          )}

          {outgoing.length > 0 && (
            <Section title="Pending Invites">
              {outgoing.map((c) => (
                <ContractorCard
                  key={c._id}
                  contractor={c}
                  variant="outgoing"
                  onCancel={handleCancel}
                  loadingId={actionLoadingId}
                />
              ))}
            </Section>
          )}

          {active.length > 0 && (
            <Section title="Active Contractors">
              {active.map((c) => (
                <ContractorCard
                  key={c._id}
                  contractor={c}
                  variant="active"
                  onDelete={handleDelete}
                  loadingId={actionLoadingId}
                />
              ))}
            </Section>
          )}

          {!incoming.length &&
            !outgoing.length &&
            !active.length && (
              <div className="rounded-xl border bg-white p-8 text-center text-sm text-gray-500">
                No contractors yet.
                <div className="mt-3">
                  <button
                    onClick={() => setInviteOpen(true)}
                    className="text-indigo-600 hover:underline"
                  >
                    Invite your first contractor →
                  </button>
                </div>
              </div>
            )}
        </>
      )}

      <InviteCompanyModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={loadContracts}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}